// © Microsoft Corporation. All rights reserved.

import { ConnectorNamespaces, CookieConsentStatus, SignedInStatus } from "@msnews/experiences-constants";
import { ExperiencesSharedStateActions, ExperiencesSharedStateConnector } from "@msnews/experiences-shared-state";

import { PageBase } from "@msnews/experiences-page-base";
import { SignInStateResolver } from "./GetSignInState";
import { appEnvironment } from "@msnews/core";
import { defaultOneServiceOcid } from "./AuthConstants";

/**
 * Initiates the single sign on (SSO) process for RPS.
 * Launches hidden iframe to fire a silent sign-in URL to an identity provider (e.g. Live).
 * Sign-in URLs typically embed a return URL. When the identity provider completes,
 * the user is redirected to the return URL, which currently must be on the msn.com domain. The RPS attribute on that return URL page
 * process the sign-in payload and produce an appropriate auth cookie.
 * After the processing is done, the hidden iframe will notify the parent window
 * (who called the sso function) whether the sign-in succeeded or not and will pass back some basic user information via PostMessage.
 * The parent window will detect the notification, remove the iframe, and initiate any update requests.
 *
 * Important:   This method expects the iframe to send a message with 'sso_completed' or 'sso_failed' as data.
 */

/**
 * Window elements that are necessary to use SSO
 */
declare global {
    interface Window {
        /**
         * Allows attaching a callback to an event
         */
        attachEvent?: any;

        /**
         * Removes an attached callback from an event
         */
        detachEvent?: any;

        /**
         * The SSO Data returned by the SSO call, which contains user information like name, email, etc.
         */
        MeControlSSOData?: Promise<any>;
    }
}

/**
 * The string returned by the post frame message following a successful SSO signin
 */
export const ssoCompletedMessage = "sso_completed";

/**
 * The string returned by the post frame message following a failed SSO sign in attempt
 */
export const ssoFailedMessage = "sso_failed";

/**
 * The signInProperties returned by the sso call
 */
export interface SSOSignInProperties {
    /**
     * The returned status of the sso request, either "sso_completed" or "sso_failed"
     */
    ssoStatus: string;

    /**
     * The CID for the user if signed in
     */
    cid?: string;

    /**
     * The first name of the user if signed in
     */
    firstName?: string;

    /**
     * Tha last name of the user if signed in
     */
    lastName?: string;

    /**
     * The member name of the user, i.e. the name they use to sign into their MSA account
     */
    memberName?: string;
}

/**
 * Initiates the single-sign-on (SSO) process for RPS
 * @param onComplete The function to be called when the SSO process is complete
 */
export function initiateSSO(onComplete: (ssoData: SSOSignInProperties) => void): void {
    // Obtain the current cookie consent staus from the ExperiencesSharedState connector
    const sharedStateConnector: ExperiencesSharedStateConnector = PageBase.getInstance().rootReducer.connector(ConnectorNamespaces.SharedState) as ExperiencesSharedStateConnector;
    const cookieConsentState: CookieConsentStatus = sharedStateConnector.getCurrentState().cookieConsentStatus;

    // If the user has not given consent to use cookies, do not make the sso call
    if (cookieConsentState !== CookieConsentStatus.NotRequired) {
        // do not make the below call when rendering cookie banner. This is to avoid dropping cookies on page before user accepts the cookie banner
        return;
    }

    const silentSignInUrl: string = generateSilentSignInUrl();

    // create the iframe that will load the silentSignInUrl
    const iframe: HTMLElement = window.document.createElement("IFRAME");
    iframe.id = "sso_frame";
    iframe.style.width = "1px";
    iframe.style.height = "1px";
    iframe.style.position = "absolute";
    iframe.style.display = "none";

    // @ts-ignore - src is not a common property of HTMLElements but does exist for iframe
    iframe.src = silentSignInUrl;
    window.document.body.appendChild(iframe);

    // Add the event listener to listen for the postMessage sent by the iframe
    let messageEventType: string;
    let removeEventHandler: any;

    // This is triggered when the IFRAME calls postMessage.
    const eventHandler = (e: any) => {
        const ssoData = e.data;
        // If the event caught was sent from the sso iframe
        if (ssoData && ssoData.ssoStatus) {
            // Resolve the signin state awaiter
            SignInStateResolver();

            // Remove the event handler and iframe from the page
            removeEventHandler.call(window, messageEventType, eventHandler);
            window.document.body.removeChild(iframe);

            // Set the page sign in state based on the SSO data returned
            if (sharedStateConnector) {
                const previousSignInStatus: SignedInStatus = sharedStateConnector.getCurrentState().signedIn;
                const signInStatus: SignedInStatus = (ssoData.ssoStatus === ssoCompletedMessage) ? SignedInStatus.SignedIn : SignedInStatus.NotSignedIn;
                if (previousSignInStatus !== signInStatus) {
                    ExperiencesSharedStateActions.signedInStatus.getActionSender(sharedStateConnector).send(signInStatus);
                }
            }

            // Call the onComplete callback to process the sso results
            onComplete(ssoData);
        }
    };

    if (window.addEventListener) {
        // More modern browsers
        messageEventType = "message";
        window.addEventListener(messageEventType, eventHandler, false);
        removeEventHandler = window.removeEventListener;
    } else {
        // IE8 and below
        messageEventType = "onmessage";
        window.attachEvent(messageEventType, eventHandler);
        removeEventHandler = window.detachEvent;
    }
}

/**
 * Initiates the SSO process and sets up a globally available promise that returns the SSO data
 * SSO data can then be read from window.MeControlSSOData
 */
export function SetupGlobalSSO(): void {
    let MeControlSSODataResolver: (ssoData: any) => any;
    const meControlSSOData: Promise<any> = new Promise((resolve) => {
        MeControlSSODataResolver = resolve;
    });

    window.MeControlSSOData = meControlSSOData;

    // begin the SSO process
    initiateSSO(
        (ssoData) => {
            MeControlSSODataResolver(ssoData);
        }
    );
}

/**
 * Generates the URL used to silently sign in the user
 */
function generateSilentSignInUrl(): string {
    const market: string = appEnvironment.CurrentMarket;
    let domain: string = window.location.hostname;
    // for local testing, if the domain would send you to a non-existant site go to the www.msn.com site instead
    if (domain.toLowerCase().includes("localhost") || domain.toLowerCase().includes("windows.msn.com") || domain.toLowerCase().includes("ntp.msn.com")) {
        domain = "www.msn.com";
    }

    const replyUrl: string = (appEnvironment.UseOneServiceAuthEndpoints || appEnvironment.UseOneServiceAnonAuthEndpoints) ?
        encodeURIComponent(`https://api.msn.com/auth/cookie/silentpassport?apikey=${appEnvironment.OneServiceApiKey}&ocid=${defaultOneServiceOcid}&secure=true`)
        : encodeURIComponent(`https://${domain}/${market}/homepage/secure/silentpassport?secure=true`);
    // use MBI_SSL for the OneService endpoint and lbi for the Prime endpoint
    const wpParam: string = (appEnvironment.UseOneServiceAuthEndpoints || appEnvironment.UseOneServiceAnonAuthEndpoints) ? "MBI_SSL" : "lbi";

    const silentSignInUrl = `https://login.live.com/login.srf?wa=wsignin1.0&checkda=1&wp=${wpParam}&mkt=${market}&wreply=${replyUrl}`;

    return silentSignInUrl;
}